import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import CompanyAdminSidebar from '@/components/CompanyAdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  CalendarDays,
  Clock,
  Palmtree,
  CalendarOff,
  Plus,
  Trash2,
  Users,
  CheckCircle,
  XCircle,
  Thermometer,
  Briefcase,
  XSquare,
  MessageSquare,
  ArrowLeftRight,
  StickyNote,
  Crown,
  Grid3X3,
  Send,
  GripVertical,
  Move,
  Heart,
  Sun,
  Sunset,
  Sunrise,
  Settings,
  Pencil,
  X,
  Repeat
} from 'lucide-react';
import { format, addDays, startOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, getDay } from 'date-fns';
import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor, rectIntersection } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import AIRosterAssistant from '@/components/AIRosterAssistant';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preset color options
const PRESET_COLORS = [
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#ec4899', // pink
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#f97316'  // orange
];

// Helper to adjust color brightness
const adjustColor = (hex, amount) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

// Entry type definitions with colors and icons
const ENTRY_TYPES = {
  shift: {
    id: 'shift',
    label: 'Worked Time',
    shortLabel: 'Shift',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    textColor: 'text-white',
    icon: Clock,
    hasTime: true
  },
  annual_leave: {
    id: 'annual_leave',
    label: 'Annual Leave',
    shortLabel: 'AL',
    color: 'from-emerald-400 to-teal-500',
    bgColor: 'bg-gradient-to-br from-emerald-400 to-teal-500',
    textColor: 'text-white',
    icon: Palmtree,
    hasTime: false
  },
  sick: {
    id: 'sick',
    label: 'Sick Day',
    shortLabel: 'Sick',
    color: 'from-rose-400 to-red-500',
    bgColor: 'bg-gradient-to-br from-rose-400 to-red-500',
    textColor: 'text-white',
    icon: Thermometer,
    hasTime: false
  },
  holiday: {
    id: 'holiday',
    label: 'Public Holiday Taken',
    shortLabel: 'PH Off',
    color: 'from-amber-400 to-orange-500',
    bgColor: 'bg-gradient-to-br from-amber-400 to-orange-500',
    textColor: 'text-white',
    icon: Sun,
    hasTime: false
  },
  holiday_worked: {
    id: 'holiday_worked',
    label: 'Public Holiday Worked',
    shortLabel: 'PH Work',
    color: 'from-amber-500 to-red-500',
    bgColor: 'bg-gradient-to-br from-amber-500 to-red-500',
    textColor: 'text-white',
    icon: Sunset,
    hasTime: true
  },
  bereavement: {
    id: 'bereavement',
    label: 'Bereavement Leave',
    shortLabel: 'Bereav',
    color: 'from-slate-500 to-slate-700',
    bgColor: 'bg-gradient-to-br from-slate-500 to-slate-700',
    textColor: 'text-white',
    icon: Heart,
    hasTime: false
  },
  day_off: {
    id: 'day_off',
    label: 'Day Off',
    shortLabel: 'Off',
    color: 'from-slate-300 to-slate-400',
    bgColor: 'bg-gradient-to-br from-slate-300 to-slate-400',
    textColor: 'text-slate-700',
    icon: CalendarOff,
    hasTime: false
  },
  rdo: {
    id: 'rdo',
    label: 'RDO',
    shortLabel: 'RDO',
    color: 'from-violet-400 to-purple-500',
    bgColor: 'bg-gradient-to-br from-violet-400 to-purple-500',
    textColor: 'text-white',
    icon: XSquare,
    hasTime: false
  },
  leave: {
    id: 'leave',
    label: 'Leave',
    shortLabel: 'Leave',
    color: 'from-teal-400 to-cyan-500',
    bgColor: 'bg-gradient-to-br from-teal-400 to-cyan-500',
    textColor: 'text-white',
    icon: Briefcase,
    hasTime: false
  }
};

// Safe date formatter
const safeFormatDate = (dateValue, formatString, fallback = 'N/A') => {
  try {
    if (!dateValue) return fallback;
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return fallback;
    return format(date, formatString);
  } catch {
    return fallback;
  }
};

// Default preset shifts (used when no custom presets exist)
const DEFAULT_PRESETS = [
  { id: 'default-1', name: '4am', start_time: '04:00', color: '#3b82f6' },
  { id: 'default-2', name: '5am', start_time: '05:00', color: '#3b82f6' },
  { id: 'default-3', name: '6am', start_time: '06:00', color: '#3b82f6' },
  { id: 'default-4', name: '7am', start_time: '07:00', color: '#3b82f6' },
  { id: 'default-5', name: '8am', start_time: '08:00', color: '#3b82f6' },
  { id: 'default-6', name: '9am', start_time: '09:00', color: '#3b82f6' }
];

// Draggable Palette Item Component
const PaletteItem = ({ type, shiftTime, shiftPreset, children }) => {
  const dragId = shiftPreset 
    ? `palette-preset-${shiftPreset.id}` 
    : (shiftTime ? `palette-shift-${shiftTime}` : `palette-${type}`);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId,
    data: { 
      type: 'palette',
      entryType: type || 'shift',
      shiftTime: shiftPreset?.start_time || shiftTime || null,
      presetId: shiftPreset?.id || null
    }
  });
  
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : 'auto',
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...listeners} 
      {...attributes} 
      className={`cursor-grab active:cursor-grabbing ${isDragging ? 'ring-2 ring-offset-2 ring-indigo-500' : ''}`}
    >
      {children}
    </div>
  );
};

// Droppable Cell Component
const DroppableCell = ({ id, children, isActive }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  
  return (
    <div 
      ref={setNodeRef} 
      className={`h-full min-h-[60px] transition-all duration-200 rounded-lg ${
        isOver ? 'ring-2 ring-indigo-500 ring-offset-2 bg-indigo-100 scale-105' : ''
      } ${isActive ? 'bg-indigo-50/50' : ''}`}
    >
      {children}
    </div>
  );
};

const RosterBuilder = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('schedule');
  const [viewMode, setViewMode] = useState('weekly');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [roster, setRoster] = useState(null);
  const [earlyCounts, setEarlyCounts] = useState({ counts: {}, early_time: null });
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [dailyNotes, setDailyNotes] = useState({});
  const [noteText, setNoteText] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  
  // Dialog states
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [entryForm, setEntryForm] = useState({
    entry_type: 'shift',
    start_time: '09:00',
    end_time: '',
    comment: ''
  });
  const [saving, setSaving] = useState(false);
  const [shiftSwaps, setShiftSwaps] = useState([]);
  const [allShiftSwaps, setAllShiftSwaps] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [processingLeave, setProcessingLeave] = useState(null);
  
  // Pattern management
  const [showPatternDialog, setShowPatternDialog] = useState(false);
  const [selectedPatternEmployee, setSelectedPatternEmployee] = useState(null);
  const [patternForm, setPatternForm] = useState({ pattern_name: '', pattern_days: [] });
  const [applyingPattern, setApplyingPattern] = useState(false);
  const [patternApplyRange, setPatternApplyRange] = useState({ start_date: '', end_date: '' });
  const [showPatternInEntry, setShowPatternInEntry] = useState(false);
  
  // Drag state
  const [activeId, setActiveId] = useState(null);
  const [draggedData, setDraggedData] = useState(null);
  
  // Shift presets management
  const [shiftPresets, setShiftPresets] = useState([]);
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetForm, setPresetForm] = useState({ name: '', start_time: '09:00', end_time: '', color: '#3b82f6' });
  const [savingPreset, setSavingPreset] = useState(false);
  
  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Get all days for current view (week or month)
  const getViewDays = useCallback(() => {
    if (viewMode === 'monthly') {
      return eachDayOfInterval({ 
        start: startOfMonth(currentMonth), 
        end: endOfMonth(currentMonth) 
      });
    }
    // Weekly view - 7 days starting from weekStart
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [viewMode, currentMonth, weekStart]);

  // Get abbreviated employee name for mobile display (e.g., "Joh G" for "John Grey")
  const getAbbreviatedName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) {
      // Single name - show first 3 chars
      return parts[0].substring(0, 3);
    }
    // First 3 chars of first name + first char of last name
    const firstName = parts[0].substring(0, 3);
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}`;
  };

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      const params = viewMode === 'monthly' 
        ? { month: format(currentMonth, 'yyyy-MM') }
        : { week_start: format(weekStart, 'yyyy-MM-dd') };
      
      const periodEnd = viewMode === 'monthly' ? endOfMonth(currentMonth) : addDays(weekStart, 6);
      const [rosterRes, requestsRes, swapsRes, leaveRes, presetsRes, earlyRes] = await Promise.all([
        axios.get(`${API}/roster`, { params }),
        axios.get(`${API}/roster/requests`),
        axios.get(`${API}/roster/shift-swaps`).catch(() => ({ data: [] })),
        axios.get(`${API}/annual-leave/requests`).catch(() => ({ data: [] })),
        axios.get(`${API}/roster/shift-presets`).catch(() => ({ data: [] })),
        axios.get(`${API}/roster/ai/early-start-counts`, { params: { end_date: format(periodEnd, 'yyyy-MM-dd') } }).catch(() => ({ data: { counts: {}, early_time: null } }))
      ]);
      setEarlyCounts(earlyRes.data);
      setRoster(rosterRes.data);
      setRequests(requestsRes.data);
      setAllShiftSwaps(swapsRes.data);
      setShiftSwaps(swapsRes.data.filter(s => s.status === 'pending_manager'));
      setLeaveRequests(leaveRes.data);
      setShiftPresets(presetsRes.data.length > 0 ? presetsRes.data : DEFAULT_PRESETS);
      
      const notesMap = {};
      (rosterRes.data.daily_notes || []).forEach(note => {
        notesMap[note.date] = note;
      });
      setDailyNotes(notesMap);
    } catch (error) {
      console.error('Failed to fetch roster:', error);
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, [weekStart, currentMonth, viewMode]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const navigateWeek = (direction) => setWeekStart(prev => addDays(prev, direction * 7));
  const navigateMonth = (direction) => setCurrentMonth(prev => direction > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
  const getDateForDay = (dayIndex) => addDays(weekStart, dayIndex);
  const getMonthDays = () => eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });

  const getEntryForCell = useCallback((employeeId, dayIndex) => {
    if (!roster?.entries) return null;
    const date = format(getDateForDay(dayIndex), 'yyyy-MM-dd');
    return roster.entries.find(e => e.user_id === employeeId && e.date === date);
  }, [roster, weekStart]);

  // Get entry for a specific date
  const getEntryForDate = useCallback((employeeId, date) => {
    if (!roster?.entries) return null;
    const dateStr = format(date, 'yyyy-MM-dd');
    return roster.entries.find(e => e.user_id === employeeId && e.date === dateStr);
  }, [roster]);

  // Open entry dialog for a specific date
  const openEntryDialogForDate = (employee, date, existingEntry = null) => {
    const existing = existingEntry || getEntryForDate(employee.id, date);
    setSelectedCell({
      employee,
      dayIndex: null, // Not using dayIndex for monthly view
      date: format(date, 'yyyy-MM-dd'),
      existing
    });
    
    if (existing) {
      setEntryForm({
        entry_type: existing.entry_type,
        start_time: existing.start_time || '09:00',
        end_time: existing.end_time || '',
        comment: existing.comment || ''
      });
    } else {
      setEntryForm({
        entry_type: 'shift',
        start_time: '09:00',
        end_time: '',
        comment: ''
      });
    }
    setShowEntryDialog(true);
    setShowPatternInEntry(false);
  };

  const openEntryDialog = (employee, dayIndex, existingEntry = null) => {
    const existing = existingEntry || getEntryForCell(employee.id, dayIndex);
    setSelectedCell({
      employee,
      dayIndex,
      date: format(getDateForDay(dayIndex), 'yyyy-MM-dd'),
      existing
    });
    
    if (existing) {
      setEntryForm({
        entry_type: existing.entry_type,
        start_time: existing.start_time || '09:00',
        end_time: existing.end_time || '',
        comment: existing.comment || ''
      });
    } else {
      setEntryForm({
        entry_type: 'shift',
        start_time: '09:00',
        end_time: '',
        comment: ''
      });
    }
    setShowEntryDialog(true);
    setShowPatternInEntry(false);
  };

  const openNoteDialog = (date) => {
    const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
    setEditingNote(dateStr);
    setNoteText(dailyNotes[dateStr]?.note || '');
    setShowNoteDialog(true);
  };

  const handleSaveNote = async () => {
    if (!editingNote) return;
    setSaving(true);
    try {
      await axios.post(`${API}/roster/notes`, { date: editingNote, note: noteText });
      toast.success('Note saved');
      setShowNoteDialog(false);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEntry = async () => {
    if (!selectedCell) return;
    setSaving(true);
    try {
      await axios.post(`${API}/roster`, {
        user_id: selectedCell.employee.id,
        date: selectedCell.date,
        entry_type: entryForm.entry_type,
        start_time: ENTRY_TYPES[entryForm.entry_type]?.hasTime ? entryForm.start_time : null,
        end_time: ENTRY_TYPES[entryForm.entry_type]?.hasTime && entryForm.end_time ? entryForm.end_time : null,
        comment: entryForm.comment || null
      });
      toast.success('Roster updated');
      setShowEntryDialog(false);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!selectedCell?.existing) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/roster/${selectedCell.existing.id}`);
      toast.success('Entry deleted');
      setShowEntryDialog(false);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const handleProcessRequest = async (requestId, status) => {
    try {
      await axios.put(`${API}/roster/requests/${requestId}?status=${status}`);
      toast.success(`Request ${status}`);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    }
  };

  const handleSwapApproval = async (swapId, action) => {
    try {
      await axios.put(`${API}/roster/shift-swaps/${swapId}/manager-response?action=${action}`);
      toast.success(`Swap ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process swap');
    }
  };

  const handleProcessLeave = async (requestId, action) => {
    setProcessingLeave(requestId);
    try {
      const response = await axios.put(`${API}/annual-leave/requests/${requestId}?action=${action}`);
      toast.success(response.data.message);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process leave request');
    } finally {
      setProcessingLeave(null);
    }
  };

  // Pattern functions
  const openPatternDialog = async (employee) => {
    setSelectedPatternEmployee(employee);
    try {
      const response = await axios.get(`${API}/roster/patterns/${employee.id}`);
      if (response.data?.pattern_days?.length > 0) {
        setPatternForm({ pattern_name: response.data.pattern_name || '', pattern_days: response.data.pattern_days });
      } else {
        setPatternForm({
          pattern_name: 'Standard Week',
          pattern_days: days.map((_, i) => i < 5 
            ? { day_type: 'shift', start_time: '09:00', end_time: '' }
            : { day_type: 'day_off', start_time: null, end_time: null })
        });
      }
    } catch {
      setPatternForm({
        pattern_name: 'Standard Week',
        pattern_days: days.map((_, i) => i < 5 
          ? { day_type: 'shift', start_time: '09:00', end_time: '' }
          : { day_type: 'day_off', start_time: null, end_time: null })
      });
    }
    setPatternApplyRange({ start_date: format(weekStart, 'yyyy-MM-dd'), end_date: format(addDays(weekStart, 27), 'yyyy-MM-dd') });
    setShowPatternDialog(true);
  };

  const handleSavePattern = async () => {
    const empId = selectedPatternEmployee?.id || selectedCell?.employee?.id;
    if (!empId) return;
    setSaving(true);
    try {
      await axios.post(`${API}/roster/patterns/${empId}`, patternForm);
      toast.success('Pattern saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pattern');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyPattern = async () => {
    const empId = selectedPatternEmployee?.id || selectedCell?.employee?.id;
    const range = patternApplyRange;
    if (!empId || !range.start_date || !range.end_date) return;
    setApplyingPattern(true);
    try {
      const res = await axios.post(`${API}/roster/patterns/${empId}/apply`, {
        user_id: empId,
        start_date: range.start_date,
        end_date: range.end_date
      });
      toast.success(res.data.message || 'Pattern applied');
      setShowPatternDialog(false);
      setShowEntryDialog(false);
      setShowPatternInEntry(false);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply pattern');
    } finally {
      setApplyingPattern(false);
    }
  };

  // Shift Preset Management
  const openPresetDialog = (preset = null) => {
    if (preset) {
      setEditingPreset(preset);
      setPresetForm({
        name: preset.name,
        start_time: preset.start_time,
        end_time: preset.end_time || '',
        color: preset.color || '#3b82f6'
      });
    } else {
      setEditingPreset(null);
      setPresetForm({ name: '', start_time: '09:00', end_time: '', color: '#3b82f6' });
    }
    setShowPresetDialog(true);
  };

  const handleSavePreset = async () => {
    if (!presetForm.name || !presetForm.start_time) {
      toast.error('Name and start time are required');
      return;
    }
    
    setSavingPreset(true);
    try {
      if (editingPreset && !editingPreset.id.startsWith('default-')) {
        // Update existing preset
        await axios.put(`${API}/roster/shift-presets/${editingPreset.id}`, presetForm);
        toast.success('Preset updated');
      } else {
        // Create new preset
        await axios.post(`${API}/roster/shift-presets`, presetForm);
        toast.success('Preset created');
      }
      setShowPresetDialog(false);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const handleDeletePreset = async (presetId) => {
    if (presetId.startsWith('default-')) {
      toast.error('Cannot delete default presets. Create custom ones to replace them.');
      return;
    }
    
    try {
      await axios.delete(`${API}/roster/shift-presets/${presetId}`);
      toast.success('Preset deleted');
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete preset');
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setDraggedData(active.data.current);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setDraggedData(null);
    
    if (!over) return;
    
    const activeData = active.data.current;
    const overId = over.id;
    
    // Parse drop target: format is "drop__{employeeId}__{dayIndexOrDate}"
    // Uses __ separator to avoid conflicts with UUID dashes and date dashes
    if (!overId.startsWith('drop__')) return;
    
    const parts = overId.split('__');
    // parts = ["drop", employeeId, dayIndexOrDate]
    const targetEmployeeId = parts[1];
    const dayOrDate = parts[2];
    
    let targetDate;
    if (dayOrDate.includes('-')) {
      // Monthly view: dayOrDate is a date string like "2026-03-30"
      targetDate = dayOrDate;
    } else {
      // Weekly view: dayOrDate is a numeric index
      targetDate = format(getDateForDay(parseInt(dayOrDate)), 'yyyy-MM-dd');
    }
    
    try {
      // Handle palette drop (new entry)
      if (activeData.type === 'palette') {
        const entryType = activeData.entryType;
        const shiftTime = activeData.shiftTime;
        
        toast.loading('Adding entry...');
        await axios.post(`${API}/roster`, {
          user_id: targetEmployeeId,
          date: targetDate,
          entry_type: entryType,
          start_time: shiftTime || (ENTRY_TYPES[entryType]?.hasTime ? '09:00' : null),
          end_time: null,
          comment: null
        });
        toast.dismiss();
        toast.success('Entry added!');
        fetchRoster();
        return;
      }
      
      // Handle existing entry drag (move/swap)
      if (activeData.type === 'existing') {
        const entry = activeData.entry;
        
        // Don't do anything if dropped in same cell
        if (entry.user_id === targetEmployeeId && entry.date === targetDate) return;
        
        const existingEntry = roster?.entries?.find(e => e.user_id === targetEmployeeId && e.date === targetDate);
        
        if (existingEntry) {
          // Swap entries
          toast.loading('Swapping entries...');
          await axios.post(`${API}/roster`, {
            user_id: targetEmployeeId,
            date: targetDate,
            entry_type: entry.entry_type,
            start_time: entry.start_time,
            end_time: entry.end_time,
            comment: entry.comment
          });
          await axios.post(`${API}/roster`, {
            user_id: entry.user_id,
            date: entry.date,
            entry_type: existingEntry.entry_type,
            start_time: existingEntry.start_time,
            end_time: existingEntry.end_time,
            comment: existingEntry.comment
          });
          toast.dismiss();
          toast.success('Entries swapped!');
        } else {
          // Move entry
          toast.loading('Moving entry...');
          await axios.delete(`${API}/roster/${entry.id}`);
          await axios.post(`${API}/roster`, {
            user_id: targetEmployeeId,
            date: targetDate,
            entry_type: entry.entry_type,
            start_time: entry.start_time,
            end_time: entry.end_time,
            comment: entry.comment
          });
          toast.dismiss();
          toast.success('Entry moved!');
        }
        fetchRoster();
      }
    } catch (error) {
      toast.dismiss();
      console.error('Drag error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update roster');
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setDraggedData(null);
  };

  // Existing Entry Draggable
  const ExistingEntryDraggable = ({ entry, children }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id: `existing-${entry.id}`,
      data: { type: 'existing', entry }
    });
    
    const style = transform ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      zIndex: isDragging ? 1000 : 'auto',
      opacity: isDragging ? 0.4 : 1,
    } : undefined;

    return (
      <div ref={setNodeRef} style={style} {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing">
        {children}
      </div>
    );
  };

  // Render cell content
  const renderCell = (employee, dayIndex) => {
    const entry = getEntryForCell(employee.id, dayIndex);
    const dropId = `drop__${employee.id}__${dayIndex}`;
    const isToday = format(getDateForDay(dayIndex), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
    
    return (
      <DroppableCell id={dropId} isActive={isToday}>
        {entry ? (
          <ExistingEntryDraggable entry={entry}>
            <div
              onClick={() => openEntryDialog(employee, dayIndex, entry)}
              className={`w-full h-full min-h-[60px] p-2 flex flex-col items-center justify-center gap-1 rounded-lg cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${ENTRY_TYPES[entry.entry_type]?.bgColor || 'bg-slate-300'} ${ENTRY_TYPES[entry.entry_type]?.textColor || 'text-slate-700'}`}
              data-testid={`cell-${employee.id}-${dayIndex}`}
            >
              {ENTRY_TYPES[entry.entry_type]?.icon && React.createElement(ENTRY_TYPES[entry.entry_type].icon, { className: 'w-4 h-4' })}
              {entry.entry_type === 'shift' || entry.entry_type === 'holiday_worked' ? (
                <span className="text-xs font-mono font-bold">{entry.start_time}</span>
              ) : (
                <span className="text-[10px] font-bold uppercase">{ENTRY_TYPES[entry.entry_type]?.shortLabel}</span>
              )}
              {entry.comment && <MessageSquare className="w-3 h-3 opacity-60" />}
            </div>
          </ExistingEntryDraggable>
        ) : (
          <button
            onClick={() => openEntryDialog(employee, dayIndex)}
            className="w-full h-full min-h-[60px] flex items-center justify-center text-slate-300 hover:bg-indigo-50 hover:text-indigo-400 transition-all rounded-lg border-2 border-dashed border-transparent hover:border-indigo-200"
            data-testid={`cell-${employee.id}-${dayIndex}`}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </DroppableCell>
    );
  };

  // Render cell content for a specific date (used in monthly view)
  const renderCellForDate = (employee, date) => {
    const entry = getEntryForDate(employee.id, date);
    const dateStr = format(date, 'yyyy-MM-dd');
    const dropId = `drop__${employee.id}__${dateStr}`;
    const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
    const isWeekend = getDay(date) === 0 || getDay(date) === 6;
    const isCompact = viewMode === 'monthly';
    
    return (
      <DroppableCell id={dropId} isActive={isToday}>
        {entry ? (
          <ExistingEntryDraggable entry={entry}>
            <div
              onClick={() => openEntryDialogForDate(employee, date, entry)}
              className={`w-full h-full ${isCompact ? 'min-h-[40px] p-1' : 'min-h-[60px] p-2'} flex flex-col items-center justify-center gap-0.5 rounded-lg cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${ENTRY_TYPES[entry.entry_type]?.bgColor || 'bg-slate-300'} ${ENTRY_TYPES[entry.entry_type]?.textColor || 'text-slate-700'}`}
              data-testid={`cell-${employee.id}-${dateStr}`}
            >
              {ENTRY_TYPES[entry.entry_type]?.icon && React.createElement(ENTRY_TYPES[entry.entry_type].icon, { className: isCompact ? 'w-3 h-3' : 'w-4 h-4' })}
              {entry.entry_type === 'shift' || entry.entry_type === 'holiday_worked' ? (
                <span className={`${isCompact ? 'text-[10px]' : 'text-xs'} font-mono font-bold`}>{entry.start_time}</span>
              ) : (
                <span className={`${isCompact ? 'text-[8px]' : 'text-[10px]'} font-bold uppercase`}>{ENTRY_TYPES[entry.entry_type]?.shortLabel}</span>
              )}
            </div>
          </ExistingEntryDraggable>
        ) : (
          <button
            onClick={() => openEntryDialogForDate(employee, date)}
            className={`w-full h-full ${isCompact ? 'min-h-[40px]' : 'min-h-[60px]'} flex items-center justify-center ${isWeekend ? 'text-slate-200 bg-slate-50' : 'text-slate-300'} hover:bg-indigo-50 hover:text-indigo-400 transition-all rounded-lg border-2 border-dashed border-transparent hover:border-indigo-200`}
            data-testid={`cell-${employee.id}-${dateStr}`}
          >
            <Plus className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'}`} />
          </button>
        )}
      </DroppableCell>
    );
  };

  // Drag overlay content
  const renderDragOverlay = () => {
    if (!draggedData) return null;
    
    let config, timeLabel;
    
    if (draggedData.type === 'palette') {
      config = ENTRY_TYPES[draggedData.entryType];
      timeLabel = draggedData.shiftTime || config?.shortLabel;
    } else if (draggedData.type === 'existing') {
      config = ENTRY_TYPES[draggedData.entry.entry_type];
      timeLabel = draggedData.entry.start_time || config?.shortLabel;
    }
    
    if (!config) return null;
    
    const Icon = config.icon;
    
    return (
      <div className={`w-20 h-16 p-2 rounded-xl shadow-2xl flex flex-col items-center justify-center gap-1 ${config.bgColor} ${config.textColor} ring-2 ring-white ring-offset-2 ring-offset-indigo-500`}>
        <Move className="w-3 h-3 opacity-70" />
        <Icon className="w-4 h-4" />
        <span className="text-xs font-bold">{timeLabel}</span>
      </div>
    );
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const pendingSwaps = shiftSwaps.filter(s => s.status === 'pending_manager');
  const pendingLeave = leaveRequests.filter(r => r.status === 'pending');

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 w-full">
      <CompanyAdminSidebar />
      
      <main className="flex-1 min-w-0 p-3 md:p-6 pb-20 md:pb-8 w-full">
        <div className="w-full max-w-7xl mx-auto space-y-4 md:space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Roster Builder
            </h1>
            <p className="text-sm text-slate-500">Drag entries from the palette into the schedule</p>
          </div>

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white/80 backdrop-blur-sm border shadow-sm w-full justify-start rounded-xl p-1">
              <TabsTrigger value="schedule" className="gap-2 flex-1 md:flex-none rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-500 data-[state=active]:text-white" data-testid="schedule-tab">
                <CalendarDays className="w-4 h-4" />
                <span className="hidden sm:inline">Schedule</span>
              </TabsTrigger>
              <TabsTrigger value="requests" className="gap-2 flex-1 md:flex-none rounded-lg relative data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white" data-testid="requests-tab">
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Requests</span>
                {pendingRequests.length > 0 && <Badge className="ml-1 bg-blue-500 text-white text-xs h-5">{pendingRequests.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="swaps" className="gap-2 flex-1 md:flex-none rounded-lg relative data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-500 data-[state=active]:text-white" data-testid="swaps-tab">
                <ArrowLeftRight className="w-4 h-4" />
                <span className="hidden sm:inline">Swaps</span>
                {pendingSwaps.length > 0 && <Badge className="ml-1 bg-orange-500 text-white text-xs h-5">{pendingSwaps.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="leave" className="gap-2 flex-1 md:flex-none rounded-lg relative data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-green-500 data-[state=active]:text-white" data-testid="leave-tab">
                <Palmtree className="w-4 h-4" />
                <span className="hidden sm:inline">Leave</span>
                {pendingLeave.length > 0 && <Badge className="ml-1 bg-emerald-500 text-white text-xs h-5">{pendingLeave.length}</Badge>}
              </TabsTrigger>
            </TabsList>

            {/* Schedule Tab */}
            <TabsContent value="schedule" className="mt-4 space-y-4">
              <DndContext 
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                {/* Entry Palette */}
                <Card className="border-0 shadow-lg bg-white/90 backdrop-blur-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-5 h-5 text-indigo-500" />
                        <CardTitle className="text-base font-bold">Entry Palette</CardTitle>
                        <span className="text-xs text-slate-500 ml-2">Drag into schedule</span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openPresetDialog()}
                        className="gap-1.5 text-xs"
                        data-testid="add-preset-btn"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Shift
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-4">
                      {/* Custom Shift Presets */}
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-slate-500 uppercase">Shift Presets</span>
                        <div className="flex flex-wrap gap-2">
                          {shiftPresets.map(preset => (
                            <div key={preset.id} className="group relative flex items-center gap-1">
                              <PaletteItem type="shift" shiftPreset={preset}>
                                <div 
                                  className="px-3 py-2 rounded-lg text-white text-xs font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-1.5 cursor-grab active:cursor-grabbing"
                                  style={{ background: `linear-gradient(135deg, ${preset.color}, ${adjustColor(preset.color, -30)})` }}
                                >
                                  <Clock className="w-3 h-3" />
                                  {preset.name}
                                </div>
                              </PaletteItem>
                              {/* Edit/Delete buttons - visible on hover */}
                              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button 
                                  onClick={(e) => { e.stopPropagation(); openPresetDialog(preset); }}
                                  className="w-6 h-6 bg-white border border-slate-200 rounded-md shadow-sm flex items-center justify-center hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                  title="Edit preset"
                                  data-testid={`edit-preset-${preset.id}`}
                                >
                                  <Pencil className="w-3 h-3 text-indigo-600" />
                                </button>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); handleDeletePreset(preset.id); }}
                                  className="w-6 h-6 bg-white border border-slate-200 rounded-md shadow-sm flex items-center justify-center hover:bg-red-50 hover:border-red-300 transition-colors"
                                  title="Delete preset"
                                  data-testid={`delete-preset-${preset.id}`}
                                >
                                  <Trash2 className="w-3 h-3 text-red-600" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Entry Types */}
                      <div className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-slate-500 uppercase">Entry Types</span>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(ENTRY_TYPES).filter(([k]) => k !== 'shift').map(([key, config]) => {
                            const Icon = config.icon;
                            return (
                              <PaletteItem key={key} type={key}>
                                <div className={`px-3 py-2 rounded-lg ${config.bgColor} ${config.textColor} text-xs font-bold shadow-md hover:shadow-lg transition-shadow flex items-center gap-1.5`}>
                                  <Icon className="w-3 h-3" />
                                  {config.shortLabel}
                                </div>
                              </PaletteItem>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Navigation */}
                <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
                        <TabsList className="bg-slate-100 h-10 rounded-lg">
                          <TabsTrigger value="weekly" className="gap-2 rounded-md" data-testid="weekly-view-btn">
                            <CalendarDays className="w-4 h-4" />
                            Weekly
                          </TabsTrigger>
                          <TabsTrigger value="monthly" className="gap-2 rounded-md" data-testid="monthly-view-btn">
                            <Grid3X3 className="w-4 h-4" />
                            Monthly
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>

                      <div className="flex items-center gap-3">
                        <Button variant="outline" size="icon" className="h-10 w-10 rounded-xl" onClick={() => viewMode === 'monthly' ? navigateMonth(-1) : navigateWeek(-1)} data-testid="prev-period-btn">
                          <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <div className="flex items-center gap-2 min-w-[200px] justify-center">
                          <Calendar className="w-5 h-5 text-indigo-500" />
                          <span className="text-lg font-bold text-slate-800">
                            {viewMode === 'monthly' 
                              ? format(currentMonth, 'MMMM yyyy')
                              : `${format(weekStart, 'MMM d')} - ${format(addDays(weekStart, 6), 'MMM d')}`}
                          </span>
                        </div>
                        <Button variant="outline" size="icon" className="h-10 w-10 rounded-xl" onClick={() => viewMode === 'monthly' ? navigateMonth(1) : navigateWeek(1)} data-testid="next-period-btn">
                          <ChevronRight className="w-5 h-5" />
                        </Button>
                      </div>

                      <Button variant="outline" className="rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200 text-indigo-700"
                        onClick={() => { setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 })); setCurrentMonth(new Date()); }}>
                        Today
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Roster Grid */}
                <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm overflow-hidden">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                      {loading ? (
                        <div className="flex justify-center py-12">
                          <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent" />
                        </div>
                      ) : !roster?.employees?.length ? (
                        <div className="text-center py-12">
                          <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No employees found</p>
                        </div>
                      ) : (
                        <div style={{ minWidth: viewMode === 'monthly' ? '1200px' : '700px' }}>
                          <table className="w-full border-collapse" data-testid="roster-grid">
                            <thead className="sticky top-0 z-20">
                              <tr className="bg-gradient-to-r from-slate-100 to-indigo-50">
                                <th className="p-3 text-left text-sm font-bold text-slate-700 w-[100px] md:w-[180px] sticky left-0 bg-slate-100 z-30 border-b-2 border-b-indigo-200">
                                  Employee
                                </th>
                                {getViewDays().map((dayDate, idx) => {
                                  const isToday = format(dayDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                                  const dayName = format(dayDate, 'EEE');
                                  const isWeekend = getDay(dayDate) === 0 || getDay(dayDate) === 6;
                                  return (
                                    <th 
                                      key={format(dayDate, 'yyyy-MM-dd')} 
                                      className={`p-1 md:p-2 text-center text-xs md:text-sm font-semibold border-b-2 ${
                                        isToday 
                                          ? 'bg-gradient-to-b from-indigo-500 to-purple-500 text-white border-b-indigo-400' 
                                          : isWeekend 
                                            ? 'bg-slate-100 text-slate-500 border-b-indigo-200'
                                            : 'bg-slate-50 text-slate-600 border-b-indigo-200'
                                      }`}
                                      style={{ minWidth: viewMode === 'monthly' ? '40px' : '90px' }}
                                    >
                                      <div className="hidden md:block">{dayName}</div>
                                      <div className="md:hidden">{dayName.charAt(0)}</div>
                                      <div className={`text-xs ${isToday ? 'text-white/90' : 'text-slate-400'}`}>{format(dayDate, 'd')}</div>
                                      {isToday && <Badge className="mt-1 bg-white/20 text-white text-[10px] hidden md:inline-flex">TODAY</Badge>}
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {roster.employees.map((employee) => (
                                <tr key={employee.id} className="border-t hover:bg-slate-50/50">
                                  <td className="p-2 md:p-3 sticky left-0 bg-white z-10 border-r">
                                    <div className="flex items-center gap-2 md:gap-3">
                                      <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-xs md:text-sm font-bold flex-shrink-0">
                                        {employee.name.charAt(0).toUpperCase()}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1">
                                          <span className="font-medium text-xs md:text-sm truncate" title={employee.name}>
                                            {getAbbreviatedName(employee.name)}
                                          </span>
                                          {(employee.is_manager || employee.role === 'manager') && (
                                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 px-1">
                                              <Crown className="w-2.5 h-2.5" />
                                            </Badge>
                                          )}
                                          {earlyCounts.early_time && (
                                            <Badge
                                              variant="outline"
                                              data-testid={`early-start-count-${employee.id}`}
                                              title={`${earlyCounts.counts[employee.id] || 0} early starts (at or before ${earlyCounts.early_time}) in the last 4 weeks`}
                                              className="text-[10px] bg-orange-50 text-orange-700 border-orange-200 px-1 gap-0.5 flex-shrink-0"
                                            >
                                              <Sunrise className="w-2.5 h-2.5" />
                                              {earlyCounts.counts[employee.id] || 0}
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                      <Button variant="ghost" size="sm" className="h-6 w-6 md:h-7 md:px-2 p-0 text-xs text-indigo-600 hover:bg-indigo-50 hidden md:flex" onClick={() => openPatternDialog(employee)}>
                                        <Grid3X3 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </td>
                                  {getViewDays().map((dayDate, dayIndex) => (
                                    <td key={format(dayDate, 'yyyy-MM-dd')} className="p-0.5 md:p-1 border-l">
                                      {renderCellForDate(employee, dayDate)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                              {/* Notes Row */}
                              <tr className="border-t-2 border-indigo-200 bg-indigo-50/50">
                                <td className="p-2 md:p-3 sticky left-0 bg-indigo-50 z-10">
                                  <div className="flex items-center gap-2">
                                    <StickyNote className="w-4 h-4 md:w-5 md:h-5 text-indigo-500" />
                                    <span className="font-medium text-xs md:text-sm text-indigo-700">Notes</span>
                                  </div>
                                </td>
                                {getViewDays().map((dayDate) => {
                                  const dateStr = format(dayDate, 'yyyy-MM-dd');
                                  const note = dailyNotes[dateStr];
                                  return (
                                    <td key={dateStr} className="p-0.5 md:p-1">
                                      <button 
                                        onClick={() => openNoteDialog(dateStr)} 
                                        className={`w-full min-h-[40px] md:min-h-[50px] p-1 md:p-2 flex flex-col items-center justify-center gap-1 rounded-lg transition-all ${
                                          note ? 'bg-indigo-100 border-indigo-300' : 'bg-white border-dashed border-indigo-200 hover:border-indigo-400'
                                        } border`}
                                      >
                                        {note ? (
                                          <>
                                            <MessageSquare className="w-3 h-3 md:w-4 md:h-4 text-indigo-600" />
                                            <span className="text-[10px] md:text-xs text-indigo-700 line-clamp-1 hidden md:block">{note.note}</span>
                                          </>
                                        ) : (
                                          <Plus className="w-3 h-3 md:w-4 md:h-4 text-indigo-400" />
                                        )}
                                      </button>
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <DragOverlay>
                  {renderDragOverlay()}
                </DragOverlay>
              </DndContext>

              {/* AI Rostering Assistant */}
              <AIRosterAssistant
                weekStart={weekStart}
                currentWeekStart={weekStart}
                onApplied={() => { fetchRoster(); }}
              />
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests" className="mt-4">
              <Card className="border-0 shadow-lg bg-white/80">
                <CardHeader><CardTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-blue-500" />Shift Change Requests</CardTitle></CardHeader>
                <CardContent>
                  {requests.length === 0 ? (
                    <div className="text-center py-12 text-slate-500"><Send className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No requests</p></div>
                  ) : (
                    <div className="space-y-3">
                      {requests.map((req) => (
                        <div key={req.id} className="p-4 rounded-xl bg-slate-50 border">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold">{req.user_name}</p>
                              <p className="text-sm text-slate-600">{safeFormatDate(req.date, 'EEE, MMM d')} → {req.requested_start_time}</p>
                              {req.reason && <p className="text-sm text-slate-500 mt-1">{req.reason}</p>}
                            </div>
                            {req.status === 'pending' ? (
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleProcessRequest(req.id, 'approved')}><CheckCircle className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleProcessRequest(req.id, 'rejected')}><XCircle className="w-4 h-4" /></Button>
                              </div>
                            ) : (
                              <Badge className={req.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{req.status}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Swaps Tab */}
            <TabsContent value="swaps" className="mt-4">
              <Card className="border-0 shadow-lg bg-white/80">
                <CardHeader><CardTitle className="flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-orange-500" />Shift Swaps</CardTitle></CardHeader>
                <CardContent>
                  {allShiftSwaps.length === 0 ? (
                    <div className="text-center py-12 text-slate-500"><ArrowLeftRight className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No swaps</p></div>
                  ) : (
                    <div className="space-y-3">
                      {allShiftSwaps.map((swap) => (
                        <div key={swap.id} className="p-4 rounded-xl bg-slate-50 border">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold">{swap.requester_name} ↔ {swap.target_name}</p>
                              <p className="text-sm text-slate-600">{safeFormatDate(swap.requester_date, 'MMM d')} ↔ {safeFormatDate(swap.target_date, 'MMM d')}</p>
                            </div>
                            {swap.status === 'pending_manager' ? (
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleSwapApproval(swap.id, 'approve')}><CheckCircle className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleSwapApproval(swap.id, 'reject')}><XCircle className="w-4 h-4" /></Button>
                              </div>
                            ) : (
                              <Badge className={swap.status === 'approved' ? 'bg-green-100 text-green-700' : swap.status === 'pending_coworker' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{swap.status.replace('_', ' ')}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Leave Tab */}
            <TabsContent value="leave" className="mt-4">
              <Card className="border-0 shadow-lg bg-white/80">
                <CardHeader><CardTitle className="flex items-center gap-2"><Palmtree className="w-5 h-5 text-emerald-500" />Annual Leave Requests</CardTitle></CardHeader>
                <CardContent>
                  {leaveRequests.length === 0 ? (
                    <div className="text-center py-12 text-slate-500"><Palmtree className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No leave requests</p></div>
                  ) : (
                    <div className="space-y-3">
                      {leaveRequests.map((req) => (
                        <div key={req.id} className="p-4 rounded-xl bg-slate-50 border">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold">{req.user_name}</p>
                              <p className="text-sm text-slate-600">{safeFormatDate(req.start_date, 'MMM d')} - {safeFormatDate(req.end_date, 'MMM d, yyyy')}</p>
                              {req.reason && <p className="text-sm text-slate-500 mt-1">{req.reason}</p>}
                            </div>
                            {req.status === 'pending' ? (
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleProcessLeave(req.id, 'approve')} disabled={processingLeave === req.id}><CheckCircle className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleProcessLeave(req.id, 'reject')} disabled={processingLeave === req.id}><XCircle className="w-4 h-4" /></Button>
                              </div>
                            ) : (
                              <Badge className={req.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{req.status}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Entry Dialog */}
      <Dialog open={showEntryDialog} onOpenChange={(open) => { setShowEntryDialog(open); if (!open) setShowPatternInEntry(false); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-indigo-500" />
              {showPatternInEntry ? 'Set Pattern' : selectedCell?.existing ? 'Edit Entry' : 'Add Entry'}
            </DialogTitle>
            <DialogDescription>
              {selectedCell?.employee?.name} - {selectedCell?.date && safeFormatDate(selectedCell.date, 'EEEE, MMM d')}
              {showPatternInEntry && ' (pattern starts from this day)'}
            </DialogDescription>
          </DialogHeader>
          
          {!showPatternInEntry ? (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Entry Type</Label>
                  <Select value={entryForm.entry_type} onValueChange={(v) => setEntryForm({ ...entryForm, entry_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ENTRY_TYPES).map(([key, config]) => {
                        const Icon = config.icon;
                        return (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded ${config.bgColor}`} />
                              {config.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {ENTRY_TYPES[entryForm.entry_type]?.hasTime && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input type="time" value={entryForm.start_time} onChange={(e) => setEntryForm({ ...entryForm, start_time: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time (optional)</Label>
                      <Input type="time" value={entryForm.end_time} onChange={(e) => setEntryForm({ ...entryForm, end_time: e.target.value })} />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Comment (optional)</Label>
                  <Textarea value={entryForm.comment} onChange={(e) => setEntryForm({ ...entryForm, comment: e.target.value })} placeholder="Add a note..." rows={2} />
                </div>
              </div>

              <DialogFooter className="flex-wrap gap-2">
                {selectedCell?.existing && (
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDeleteEntry} disabled={saving}>
                    <Trash2 className="w-4 h-4 mr-1" />Delete
                  </Button>
                )}
                <Button variant="outline" onClick={() => {
                  // Load existing pattern or create blank
                  const emp = selectedCell?.employee;
                  if (emp) {
                    setSelectedPatternEmployee(emp);
                    axios.get(`${API}/roster/patterns/${emp.id}`).then(res => {
                      setPatternForm({ pattern_name: res.data.pattern_name || '', pattern_days: res.data.pattern_days || [] });
                    }).catch(() => {
                      setPatternForm({ pattern_name: '', pattern_days: [{ day_type: 'shift', start_time: '09:00' }, { day_type: 'shift', start_time: '09:00' }, { day_type: 'shift', start_time: '09:00' }, { day_type: 'shift', start_time: '09:00' }, { day_type: 'shift', start_time: '09:00' }, { day_type: 'day_off' }, { day_type: 'day_off' }] });
                    });
                    const startDate = selectedCell?.date ? format(new Date(selectedCell.date), 'yyyy-MM-dd') : format(weekStart, 'yyyy-MM-dd');
                    setPatternApplyRange({ start_date: startDate, end_date: format(addDays(new Date(startDate), 27), 'yyyy-MM-dd') });
                  }
                  setShowPatternInEntry(true);
                }} data-testid="set-pattern-btn">
                  <Repeat className="w-4 h-4 mr-1" />Set Pattern
                </Button>
                <div className="flex-1" />
                <Button variant="outline" onClick={() => setShowEntryDialog(false)}>Cancel</Button>
                <Button onClick={handleSaveEntry} disabled={saving} className="bg-gradient-to-r from-indigo-500 to-purple-500">{saving ? 'Saving...' : 'Save'}</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Pattern Name</Label>
                  <Input value={patternForm.pattern_name} onChange={(e) => setPatternForm({ ...patternForm, pattern_name: e.target.value })} placeholder="e.g., 4 on 3 off" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Pattern Days ({patternForm.pattern_days.length})</Label>
                    <Button variant="ghost" size="sm" onClick={() => setPatternForm({ ...patternForm, pattern_days: [...patternForm.pattern_days, { day_type: 'shift', start_time: '09:00' }] })}>
                      <Plus className="w-4 h-4 mr-1" />Add Day
                    </Button>
                  </div>
                  {patternForm.pattern_days.map((day, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                      <span className="w-8 text-center text-xs font-mono text-slate-400">{idx + 1}</span>
                      <Select value={day.day_type || 'shift'} onValueChange={(v) => {
                        const newDays = [...patternForm.pattern_days];
                        newDays[idx] = { ...newDays[idx], day_type: v };
                        // Clear start_time if switching to non-shift type
                        if (!ENTRY_TYPES[v]?.hasTime) {
                          newDays[idx].start_time = null;
                        }
                        setPatternForm({ ...patternForm, pattern_days: newDays });
                      }}>
                        <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(ENTRY_TYPES).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {ENTRY_TYPES[day.day_type || 'shift']?.hasTime && (
                        <Input
                          type="time"
                          value={day.start_time || '09:00'}
                          onChange={(e) => {
                            const newDays = [...patternForm.pattern_days];
                            newDays[idx] = { ...newDays[idx], start_time: e.target.value };
                            setPatternForm({ ...patternForm, pattern_days: newDays });
                          }}
                          className="w-24"
                          data-testid={`pattern-day-${idx}-time`}
                        />
                      )}
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 px-2" onClick={() => {
                        const newDays = patternForm.pattern_days.filter((_, i) => i !== idx);
                        setPatternForm({ ...patternForm, pattern_days: newDays });
                      }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                  <div className="space-y-2">
                    <Label>Apply From</Label>
                    <Input type="date" value={patternApplyRange.start_date} onChange={(e) => setPatternApplyRange({ ...patternApplyRange, start_date: e.target.value })} data-testid="pattern-start-date" />
                  </div>
                  <div className="space-y-2">
                    <Label>Apply Until</Label>
                    <Input type="date" value={patternApplyRange.end_date} onChange={(e) => setPatternApplyRange({ ...patternApplyRange, end_date: e.target.value })} data-testid="pattern-end-date" />
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowPatternInEntry(false)}>Back</Button>
                <Button variant="outline" onClick={handleSavePattern} disabled={saving} data-testid="save-pattern-btn">
                  {saving ? 'Saving...' : 'Save Pattern'}
                </Button>
                <Button onClick={async () => { await handleSavePattern(); await handleApplyPattern(); }} disabled={applyingPattern || !patternApplyRange.start_date || !patternApplyRange.end_date} className="bg-gradient-to-r from-indigo-500 to-purple-500" data-testid="apply-pattern-btn">
                  {applyingPattern ? 'Applying...' : 'Save & Apply'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><StickyNote className="w-5 h-5 text-amber-500" />Daily Note</DialogTitle>
            <DialogDescription>{editingNote && safeFormatDate(editingNote, 'EEEE, MMMM d, yyyy')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Enter note..." rows={4} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveNote} disabled={saving} className="bg-gradient-to-r from-amber-500 to-orange-500">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pattern Dialog */}
      <Dialog open={showPatternDialog} onOpenChange={setShowPatternDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule Pattern - {selectedPatternEmployee?.name}</DialogTitle>
            <DialogDescription>Set up a recurring weekly pattern</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Pattern Name</Label>
              <Input value={patternForm.pattern_name} onChange={(e) => setPatternForm({ ...patternForm, pattern_name: e.target.value })} placeholder="e.g., Standard Week" />
            </div>
            <div className="space-y-3">
              <Label>Weekly Pattern</Label>
              {days.map((day, idx) => (
                <div key={day} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                  <span className="w-12 font-medium text-slate-700">{day}</span>
                  <Select value={patternForm.pattern_days[idx]?.day_type || 'shift'} onValueChange={(v) => {
                    const newDays = [...patternForm.pattern_days];
                    newDays[idx] = { ...newDays[idx], day_type: v };
                    setPatternForm({ ...patternForm, pattern_days: newDays });
                  }}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ENTRY_TYPES).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {ENTRY_TYPES[patternForm.pattern_days[idx]?.day_type]?.hasTime && (
                    <Input type="time" value={patternForm.pattern_days[idx]?.start_time || '09:00'} onChange={(e) => {
                      const newDays = [...patternForm.pattern_days];
                      newDays[idx] = { ...newDays[idx], start_time: e.target.value };
                      setPatternForm({ ...patternForm, pattern_days: newDays });
                    }} className="w-28" />
                  )}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-4 border-t">
              <div className="space-y-2">
                <Label>Apply From</Label>
                <Input type="date" value={patternApplyRange.start_date} onChange={(e) => setPatternApplyRange({ ...patternApplyRange, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Apply Until</Label>
                <Input type="date" value={patternApplyRange.end_date} onChange={(e) => setPatternApplyRange({ ...patternApplyRange, end_date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleSavePattern} disabled={saving}>Save Pattern</Button>
            <Button onClick={handleApplyPattern} disabled={applyingPattern || !patternApplyRange.start_date || !patternApplyRange.end_date} className="bg-gradient-to-r from-indigo-500 to-purple-500">
              {applyingPattern ? 'Applying...' : 'Apply to Roster'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Preset Dialog */}
      <Dialog open={showPresetDialog} onOpenChange={setShowPresetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-500" />
              {editingPreset ? 'Edit Shift Preset' : 'Add Shift Preset'}
            </DialogTitle>
            <DialogDescription>
              Create custom shift times for quick scheduling
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Preset Name</Label>
              <Input
                value={presetForm.name}
                onChange={(e) => setPresetForm({ ...presetForm, name: e.target.value })}
                placeholder="e.g., Early Bird, Night Shift"
                data-testid="preset-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={presetForm.start_time}
                  onChange={(e) => setPresetForm({ ...presetForm, start_time: e.target.value })}
                  data-testid="preset-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time (optional)</Label>
                <Input
                  type="time"
                  value={presetForm.end_time}
                  onChange={(e) => setPresetForm({ ...presetForm, end_time: e.target.value })}
                  data-testid="preset-end-time"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setPresetForm({ ...presetForm, color })}
                    className={`w-8 h-8 rounded-lg transition-all ${
                      presetForm.color === color 
                        ? 'ring-2 ring-offset-2 ring-indigo-500 scale-110' 
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="pt-4 border-t">
              <Label className="mb-2 block">Preview</Label>
              <div 
                className="inline-flex px-4 py-2.5 rounded-lg text-white text-sm font-bold shadow-md items-center gap-2"
                style={{ background: `linear-gradient(135deg, ${presetForm.color}, ${adjustColor(presetForm.color, -30)})` }}
              >
                <Clock className="w-4 h-4" />
                {presetForm.name || 'Preset Name'}
                <span className="opacity-80">({presetForm.start_time}{presetForm.end_time ? `-${presetForm.end_time}` : ''})</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPresetDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSavePreset} 
              disabled={savingPreset || !presetForm.name || !presetForm.start_time}
              className="bg-gradient-to-r from-indigo-500 to-purple-500"
              data-testid="save-preset-btn"
            >
              {savingPreset ? 'Saving...' : (editingPreset ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RosterBuilder;
